import { atomFamily, AtomEffect } from "recoil";

import Firebase from "@app/Firebase";

import Identifiable from "./Identifiable";

async function getById<T extends Identifiable>(
  collection: string,
  id: string
): Promise<T | undefined | null> {
  const doc = await Firebase.firestore().collection(collection).doc(id).get();

  if (!doc.exists) {
    return null;
  }

  const record = doc.data() as T;
  record.id = id;

  return record;
}

export function syncStorageEffect<T extends Identifiable>(
  id: string | undefined
): (collection: string) => AtomEffect<T | undefined | null> {
  return (collection) => {
    return ({ setSelf, trigger }) => {
      if (!id) {
        return;
      }

      if (trigger === "get") {
        setSelf(getById<T>(collection, id));
      }

      const unsubscribe = Firebase.firestore()
        .collection(collection)
        .doc(id)
        .onSnapshot((doc) => {
          if (!doc.exists) {
            setSelf(null);
            return;
          }
          const record = doc.data() as T;
          record.id = id;
          setSelf(record);
        });

      return () => {
        unsubscribe();
      };
    };
  };
}

export function stateSubscriptionById<T extends Identifiable>(
  key: string,
  collection: string
) {
  return atomFamily<T | undefined | null, string | undefined>({
    key,
    default: undefined,
    effects_UNSTABLE: (id) => [syncStorageEffect<T>(id)(collection)],
  });
}
